import { useNotification } from "@context/NotificationContext";
import type { VendorLicenseRequestPayload } from "@services/licensing/types";
import { createVendorLicenseRequest } from "@services/licensingApi";
import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { paths } from "@utils/navigation";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";

const LICENSE_LIST_REFRESH_RETRY_MS = 1000;

const getErrorDescription = (err: unknown): string | undefined => {
  const error = err as {
    response?: { data?: { detail?: unknown; message?: unknown } };
    data?: { detail?: unknown; message?: unknown };
    detail?: unknown;
  };

  const detail =
    error.response?.data?.detail ??
    error.response?.data?.message ??
    error.data?.detail ??
    error.data?.message ??
    error.detail;

  return typeof detail === "string" && detail.trim().length > 0 ? detail : undefined;
};

export const useCreateVendorLicenseRequest = () => {
  const { notify } = useNotification();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: VendorLicenseRequestPayload) =>
      runTransaction(
        {
          module: "licensing",
          screen: "VendorLicenseRequest",
          action: "submit_vendor_license_request",
          entityType: "license_request",
        },
        (transactionId) =>
          createVendorLicenseRequest(payload, ...(transactionId ? [transactionId] : [])),
      ),
    onSuccess: async () => {
      await invalidateLicenseListQueries(queryClient);
      navigate(paths.vendorLicenseRequestConfirmation);
    },
    onError: (err) => {
      notifyVendorLicenseRequestError(notify, err);
    },
  });
};

const VENDOR_LICENSE_REQUEST_ERROR_TITLE =
  "licensing.notifications.vendor-license-request-submit-error-title";
const VENDOR_LICENSE_REQUEST_ERROR_DESCRIPTION =
  "licensing.notifications.vendor-license-request-submit-error-description";

export const notifyVendorLicenseRequestError = (
  notify: ReturnType<typeof useNotification>["notify"],
  err: unknown,
) => {
  notify({
    title: t(VENDOR_LICENSE_REQUEST_ERROR_TITLE),
    description: getErrorDescription(err) ?? t(VENDOR_LICENSE_REQUEST_ERROR_DESCRIPTION),
    type: "error",
  });
  console.error("MarHub Access request submit failed:", err);
};

export const invalidateLicenseListQueries = async (
  queryClient: Pick<QueryClient, "invalidateQueries" | "refetchQueries">,
) => {
  const refreshLicenseQueries = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["currentLicenses"],
      exact: false,
      refetchType: "all",
    });
    await queryClient.refetchQueries({
      queryKey: ["currentLicenses"],
      exact: false,
      type: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: ["portOperatorLicenses"],
      exact: false,
      refetchType: "all",
    });
    await queryClient.refetchQueries({
      queryKey: ["portOperatorLicenses"],
      exact: false,
      type: "all",
    });
  };

  await refreshLicenseQueries();

  globalThis.setTimeout(() => {
    void refreshLicenseQueries();
  }, LICENSE_LIST_REFRESH_RETRY_MS);
};

export {
  getErrorDescription,
  VENDOR_LICENSE_REQUEST_ERROR_DESCRIPTION,
  VENDOR_LICENSE_REQUEST_ERROR_TITLE,
};
